import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, Info, HelpCircle } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Confirm dialog — replacement for window.confirm() that matches the app's
// dark glassmorphism aesthetic. Promise-based API:
//
//   const confirm = useConfirm();
//   if (await confirm({ title: 'Delete?', message: '...', danger: true })) {
//       // user clicked confirm
//   }
//
// Wraps the app at root via <ConfirmProvider>. Any descendant can call
// useConfirm() and get back a function.
//
// ❤️ Felix mascot: pass `mood: 'confused' | 'warning' | 'happy' | null` to
// pick the matching Felix illustration. When no image is configured for
// that mood, falls back to a Lucide icon.
// ─────────────────────────────────────────────────────────────────────────────

// Configure these URLs once Felix illustrations are generated and uploaded
// to R2. Mood maps to image; missing entries fall back to icons.
const FELIX_IMAGES = {
    confused: null, // TODO: replace with R2 URL once Felix-thinking is generated
    warning:  null,
    happy:    null,
};

const ConfirmContext = createContext(null);

export const useConfirm = () => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) {
        // Fall back to native confirm if provider not mounted (defensive)
        return ({ message }) => Promise.resolve(window.confirm(message || 'Confirm?'));
    }
    return ctx;
};

const MOOD_ICON = {
    confused: HelpCircle,
    warning:  AlertTriangle,
    happy:    Info,
};

const MOOD_ACCENT = {
    confused: 'from-amber-400/20 via-cyan-400/10 to-purple-400/15',
    warning:  'from-red-500/25 via-orange-400/15 to-amber-400/10',
    happy:    'from-cyan-400/20 via-emerald-400/10 to-blue-400/15',
};

const MOOD_ICON_COLOR = {
    confused: 'text-amber-300',
    warning:  'text-red-300',
    happy:    'text-cyan-300',
};

export const ConfirmProvider = ({ children }) => {
    const [state, setState] = useState(null);
    const resolveRef = useRef(null);

    const confirm = useCallback((options = {}) => {
        return new Promise((resolve) => {
            resolveRef.current = resolve;
            setState({
                title: options.title || 'Are you sure?',
                message: options.message || '',
                confirmLabel: options.confirmLabel || 'Confirm',
                cancelLabel: options.cancelLabel || 'Cancel',
                danger: options.danger || false,
                mood: options.mood || (options.danger ? 'warning' : 'confused'),
                imageUrl: options.imageUrl || null,
            });
        });
    }, []);

    const close = useCallback((result) => {
        if (resolveRef.current) {
            resolveRef.current(result);
            resolveRef.current = null;
        }
        setState(null);
    }, []);

    // Esc to cancel, Enter to confirm
    useEffect(() => {
        if (!state) return;
        const onKey = (e) => {
            if (e.key === 'Escape') { e.preventDefault(); close(false); }
            if (e.key === 'Enter')  { e.preventDefault(); close(true); }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [state, close]);

    const Icon = state ? MOOD_ICON[state.mood] || HelpCircle : null;
    const accent = state ? MOOD_ACCENT[state.mood] : '';
    const iconColor = state ? MOOD_ICON_COLOR[state.mood] : '';
    const felixUrl = state ? (state.imageUrl || FELIX_IMAGES[state.mood]) : null;

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <AnimatePresence>
                {state && (
                    <>
                        <motion.div
                            key="confirm-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            onClick={() => close(false)}
                            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm"
                        />
                        <div className="fixed inset-0 z-[101] flex items-center justify-center p-6 pointer-events-none">
                            <motion.div
                                key="confirm-dialog"
                                initial={{ opacity: 0, scale: 0.92, y: 12 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.96, y: 8 }}
                                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                                role="alertdialog"
                                aria-modal="true"
                                className="pointer-events-auto relative w-full max-w-md rounded-2xl overflow-hidden border border-white/10 bg-[rgba(10,12,22,0.96)] backdrop-blur-2xl shadow-[0_24px_80px_rgba(0,0,0,0.7)]"
                            >
                                {/* Mood-tinted backdrop gradient */}
                                <div className={`absolute inset-0 bg-gradient-to-br ${accent} pointer-events-none`} />

                                {/* Close button */}
                                <button
                                    onClick={() => close(false)}
                                    aria-label="Close"
                                    className="absolute top-3 right-3 z-10 w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors"
                                >
                                    <X size={14} />
                                </button>

                                <div className="relative p-6 flex flex-col items-center text-center gap-4">
                                    {/* Felix portrait OR mood icon */}
                                    {felixUrl ? (
                                        <div className="relative w-24 h-24 rounded-full overflow-hidden ring-2 ring-white/15 shadow-[0_0_24px_rgba(24,181,240,0.18)]">
                                            <img src={felixUrl} alt="" className="w-full h-full object-cover" />
                                        </div>
                                    ) : (
                                        Icon && (
                                            <div className="relative w-20 h-20 flex items-center justify-center">
                                                <div className={`absolute inset-0 rounded-full blur-xl ${state.mood === 'warning' ? 'bg-red-400/30' : state.mood === 'happy' ? 'bg-cyan-400/30' : 'bg-amber-400/25'}`} />
                                                <Icon size={42} className={`relative ${iconColor}`} />
                                            </div>
                                        )
                                    )}

                                    {/* Title */}
                                    <h3 className="text-xl font-heading font-bold text-white tracking-tight">
                                        {state.title}
                                    </h3>

                                    {/* Message */}
                                    {state.message && (
                                        <p className="text-sm text-white/65 leading-relaxed max-w-sm">
                                            {state.message}
                                        </p>
                                    )}

                                    {/* Action buttons */}
                                    <div className="flex items-center gap-2 w-full mt-2">
                                        <button
                                            onClick={() => close(false)}
                                            className="flex-1 py-2.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/70 hover:text-white text-sm font-bold transition-all"
                                        >
                                            {state.cancelLabel}
                                        </button>
                                        <button
                                            onClick={() => close(true)}
                                            autoFocus
                                            className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-all ${
                                                state.danger
                                                    ? 'bg-red-500/85 hover:bg-red-500 text-white shadow-[0_0_24px_rgba(239,68,68,0.35)]'
                                                    : 'bg-cyan-400 hover:bg-cyan-300 text-black shadow-[0_0_24px_rgba(24,181,240,0.35)]'
                                            }`}
                                        >
                                            {state.confirmLabel}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </>
                )}
            </AnimatePresence>
        </ConfirmContext.Provider>
    );
};

export default ConfirmProvider;
